import {
    HTTP_INTERCEPTORS,
    HttpBackend,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer, Provider } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
    AppConfigStoreModule,
    BarcodeClipboardStoreModule,
    BarcodeScannerInstanceToken,
    BarcodeScannerNgrxService,
    BarcodeScannerService,
    CancellationComponentInstanceToken,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigNgrxService,
    ConfigService,
    ConfigStoreModule,
    ConfirmationStoreModule,
    DateManipulationCheckInstanceToken,
    DateManipulationCheckNgrxService,
    DateManipulationCheckService,
    DateManipulationCheckStoreModule,
    DeviceContextInterceptor,
    DeviceLockLostInterceptor,
    ENDPOINTS,
    EscalationInstanceToken,
    EscalationNgrxService,
    EscalationService,
    EscalationStoreModule,
    HttpOnlineInterceptor,
    InputFieldPropertiesInstanceToken,
    InputFieldPropertiesNgrxService,
    InputFieldPropertiesService,
    InputFieldPropertiesStoreModule,
    LocalStorageRecipientsService,
    LocationTrackerStoreModule,
    MeasurementToolsInstanceToken,
    MeasurementToolsNgrxService,
    MeasurementToolsService,
    MeasurementToolsStoreModule,
    MockModeInterceptorService,
    MultiTranslateHttpLoaderService,
    NotificationDataInstanceToken,
    NotificationNgrxService,
    NotificationService,
    NotificationStatusInstanceToken,
    NotificationStatusNgrxService,
    NotificationStatusService,
    NotificationStoreModule,
    OdataConfigService,
    OdataValueHelperService,
    PaceCoreModule,
    PersistenceStoreModule,
    PwaPushNotificationInstanceToken,
    PwaPushNotificationNgrxService,
    PwaPushNotificationService,
    PwaPushNotificationStoreModule,
    RecipientsInstanceToken,
    RecipientsNgrxService,
    RecipientsStoreModule,
    ServiceWorkerReadyService,
    SoftwareLicencesInstanceToken,
    SoftwareLicencesNgrxService,
    SoftwareLicencesService,
    SoftwareLicencesStoreModule,
    SynchronizationService,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesNgrxService,
    TimesheetAndExpensesService,
    TravelStatusUpdateInstanceToken,
    TravelStatusUpdateNgrxService,
    TravelStatusUpdateStoreModule,
    UserMasterInstanceToken,
    UserMasterNgrxService,
    UserMasterService,
    UserMasterStoreModule,
    UserStoreStoreModule,
    ValueHelperInstanceToken,
    ValueHelperNgrxService,
    ValueHelperService,
    ValueHelperStoreModule,
    WeeklyWorkTimeInstanceToken,
    WeeklyWorkTimeNgrxService,
    WeeklyWorkTimeService,
} from '@evorait/pace-core';
import {
    ActionUtilitiesInstanceToken,
    ActionUtilitiesService,
    ConfirmationInstanceToken,
    FormsWriteWithPaceOfflineService,
    PaceForms2Module,
} from '@evorait/pace-forms';
import {
    IndexeddbOfflineStrategyService,
    OfflineInterceptorService,
    PaceOfflineModule,
    ServiceWorkerCacheInterceptor,
} from '@evorait/pace-offline';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarConfig, ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ClipboardModule } from 'ngx-clipboard';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { APP_VERSION } from '../environments/appversion';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { ConfirmationModule } from './confirmation/confirmation.module';
import { ConfirmationDataInstanceToken } from './confirmation/interfaces/confirmation-data.interface';
import { ConfirmationNgrxService } from './confirmation/services/confirmation.ngrx.service';
import { ConfirmationSubmitEffects } from './confirmation/state/effects/confirmation-submit.effects';
import { ConfirmationService } from './confirmation/services/confirmation.service';
import { CoreModule } from './core/core.module';
import { metaReducers, reducers } from './core/reducers';
import { ConfigInterfaceService } from './core/services/config-interface/config-interface.service';
import { DeviceIdInterceptor } from './core/services/device-id/device-id.interceptor';
import { InitializationService } from './core/services/initialization/initialization.service';
import { DashboardModule } from './dashboard/dashboard.module';
import { DetailsModule } from './details/details.module';
import { TravelStatusUpdateService } from './details/travel-status-update/travel-status-update.service';
import { FormsListWrapperComponent } from './pace-forms-2-wrapper/components/forms2-list/forms-list-wrapper.component';
import { PaceForms2WrapperModule } from './pace-forms-2-wrapper/pace-forms-2-wrapper.module';
import { PaceSideMenuService } from './pace-side-menu.service';
import { CancellationComponent } from './shared/components/cancellation/cancellation.component';
import { SharedModule } from './shared/shared.module';
import { TimesheetModule } from './timesheet/timesheet.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { combineLatest, Observable, of, take } from 'rxjs';
import {
    PaceAuth0AuthenticationModule,
    PaceAuth0AuthService,
    PaceMsalAuthenticationModule,
    PaceMsalAuthService,
} from '@evorait/pace-authentication';
import { DocBoxTabbedModalModule } from './docbox-tabbed-modal/docbox-tabbed-modal.module';
import { CommonModule } from '@angular/common';
import {
    AngularPlugin,
    ApplicationinsightsAngularpluginErrorService,
} from '@microsoft/applicationinsights-angularplugin-js';
import { SmartlockerModule } from './smartlocker/smartlocker.module';

// configure defaults for @ngx-bootstrap progress bar components
export function getProgressBarConfig(): ProgressbarConfig {
    return Object.assign(new ProgressbarConfig(), {
        animate: true,
        type: 'info',
        striped: true,
    });
}

export function appInitializerFactory(
    serviceWorkerReadyService: ServiceWorkerReadyService,
    authService?: PaceAuth0AuthService | PaceMsalAuthService,
): () => Observable<unknown> {
    const dependencies: Array<Observable<unknown>> = [];

    // service worker is only activated when production is set to true
    if (environment.production) {
        dependencies.push(serviceWorkerReadyService.ready$());
    }

    const authConfigured = !!environment.msal?.clientId || !!environment.auth0?.clientId;
    if (authConfigured && authService) {
        dependencies.push(authService.ready$());
    }

    // this is mock-mode without service worker nor msal
    if (dependencies.length < 1) {
        return (): Observable<void> => of(undefined);
    } else {
        return (): Observable<unknown> => combineLatest(dependencies).pipe(take(1));
    }
}

export function HttpLoaderFactory(httpBackend: HttpBackend): MultiTranslateHttpLoaderService {
    return new MultiTranslateHttpLoaderService(new HttpClient(httpBackend), [
        { prefix: '../assets/i18n/core/', suffix: '.json' },
        { prefix: '../assets/i18n/app/', suffix: '.json' },
        { prefix: '../assets/i18n/forms/', suffix: '.json' },
    ]);
}

let interceptorProviders: Provider[] = [
    // http interceptor for intercepting authentication requests
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ServiceWorkerCacheInterceptor,
        multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptorService, multi: true },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DeviceContextInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DeviceIdInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DeviceLockLostInterceptor,
        multi: true,
    },
];

if (environment.operateInMockMode) {
    interceptorProviders.push({
        provide: HTTP_INTERCEPTORS,
        useClass: MockModeInterceptorService,
        multi: true,
    });
}
// PACE Online does not need offline service, so we only need http online interceptor to forward our request
// Cannot use ClientManagerService yet since services are not available yet at this stage, therefore appid workaround
// TODO: Decide remove if PACE PWA and PACE Online are decided to be the same software
if (environment.appId.includes('paceonline')) {
    interceptorProviders = [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpOnlineInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DeviceContextInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DeviceIdInterceptor,
            multi: true,
        },
    ];
}

const authModule = [];
if (environment.msal?.clientId) {
    authModule.push(PaceMsalAuthenticationModule.forRoot());
} else if (environment.auth0?.clientId) {
    authModule.push(PaceAuth0AuthenticationModule.forRoot());
}

@NgModule({
    bootstrap: [AppComponent],
    declarations: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ProgressbarModule,
        ModalModule,
        BsDropdownModule,
        PopoverModule,
        AccordionModule,
        RatingModule,
        TabsModule,
        CarouselModule,
        LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpBackend],
            },
        }),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: true,
                strictActionWithinNgZone: true,
                strictActionTypeUniqueness: true,
            },
        }),
        // Instrumentation must be imported after importing StoreModule (config is optional)
        // this allows to debug the store using a Chrome extension as described in the angular documentation:
        // https://ngrx.io/guide/store-devtools
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
        EffectsModule.forRoot([]),
        // Must precede every slice store module: a slice that dispatches `hydrateSlice` while its own
        // effects are being registered (e.g. from `ngrxOnInitEffects`) would otherwise lose that
        // action, because `Actions` has no replay and `PersistenceEffects` would not be subscribed yet.
        PersistenceStoreModule,
        ConfigStoreModule,
        AppConfigStoreModule,
        UserStoreStoreModule,
        ValueHelperStoreModule,
        UserMasterStoreModule,
        MeasurementToolsStoreModule,
        RecipientsStoreModule,
        BarcodeClipboardStoreModule,
        NotificationStoreModule,
        InputFieldPropertiesStoreModule,
        EscalationStoreModule,
        TravelStatusUpdateStoreModule,
        DateManipulationCheckStoreModule,
        SoftwareLicencesStoreModule,
        PwaPushNotificationStoreModule,
        LocationTrackerStoreModule,
        ConfirmationStoreModule,
        // Registered here rather than in ConfirmationStoreModule: the submit pipeline drives
        // app-level collaborators (mileage, sales quotes, OData confirmations) that pace-core may not import.
        EffectsModule.forFeature([ConfirmationSubmitEffects]),
        PersistenceStoreModule,
        RouterModule.forRoot(routes, { enableTracing: false, useHash: true }),
        CoreModule,
        SharedModule,
        PaceForms2Module.forRoot({
            notificationService: environment.enableNgRx ? NotificationNgrxService : NotificationService,
            formsWriteService: FormsWriteWithPaceOfflineService,
            formListComponent: FormsListWrapperComponent,
        }),
        ConfirmationModule,
        TimesheetModule,
        DetailsModule,
        DashboardModule,
        ClipboardModule,
        ZXingScannerModule,
        DocBoxTabbedModalModule,
        SmartlockerModule,
        TooltipModule,
        PaceCoreModule.forRoot({
            initializationService: InitializationService,
            odataConfigService: OdataConfigService,
            odataValueHelper: OdataValueHelperService,
            sideMenuService: PaceSideMenuService,
            synchronizationService: SynchronizationService,
            configInterfaceService: ConfigInterfaceService,
            environment: environment,
            appVersion: APP_VERSION,
        }),
        PaceOfflineModule.forRoot(
            [
                ENDPOINTS.ConfigSet,
                ENDPOINTS.ValueHelpers,
                ENDPOINTS.NotificationHeaders,
                ENDPOINTS.F4mReleaseLock,
                ENDPOINTS.F4mSubmitFormSet,
                ENDPOINTS.F4mSubmitParticipantCopySet,
                ENDPOINTS.MaterialStockSet,
                ENDPOINTS.InputFieldProperties,
                ENDPOINTS.CseDopSet,
                ENDPOINTS.WhatsNew,
                ENDPOINTS.WorkScheduleSet,
                ENDPOINTS.DeepInsertSet,
                ENDPOINTS.NotificationCancellation,
                ENDPOINTS.RequestNotification,
                ENDPOINTS.MaterialSerialNumbersGet,
                ENDPOINTS.MeasToolSet,
                ENDPOINTS.DocboxUploadSet,
                // Queues user-store PUT upserts so an offline write survives a restart instead of
                // being lost — see UserStoreEffects' class doc. DELETE deliberately bypasses this
                // queue (UserStoreApiService.deleteEntry sends ONLINE_HEADER), so it is not
                // affected by this registration; UserStoreEffects retries deletes itself.
                ENDPOINTS.AzUserStore,
                // Queues the tracking-permission sync POST so a save made while offline still
                // reaches SENSE once connectivity returns — see LocationTrackerEffects' class doc.
                ENDPOINTS.LocationSync,
            ],
            IndexeddbOfflineStrategyService,
        ),
        PaceForms2WrapperModule,
        ServiceWorkerModule.register('pace-pwa-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerImmediately',
        }),
        ...authModule,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        // to be provided in the app, as we do not want to provide it in the core module
        ...interceptorProviders,
        // configuration of @ngx-bootstrap modules (see below for factory)
        { provide: ProgressbarConfig, useFactory: getProgressBarConfig },
        { provide: CancellationComponentInstanceToken, useValue: CancellationComponent },
        {
            provide: ConfirmationInstanceToken,
            useExisting: environment.enableNgRx ? ConfirmationNgrxService : ConfirmationService,
        },
        {
            provide: ConfirmationDataInstanceToken,
            useExisting: environment.enableNgRx ? ConfirmationNgrxService : ConfirmationService,
        },
        {
            provide: ActionUtilitiesInstanceToken,
            useExisting: ActionUtilitiesService,
        },
        provideAppInitializer(() => {
            const serviceWorkerReadyService = inject(ServiceWorkerReadyService);
            const authService = environment.msal?.clientId
                ? inject(PaceMsalAuthService)
                : environment.auth0?.clientId
                  ? inject(PaceAuth0AuthService)
                  : undefined;
            return appInitializerFactory(serviceWorkerReadyService, authService)();
        }),
        TravelStatusUpdateService,
        {
            provide: TravelStatusUpdateInstanceToken,
            useExisting: environment.enableNgRx ? TravelStatusUpdateNgrxService : TravelStatusUpdateService,
        },
        { provide: AngularPlugin, useValue: new AngularPlugin() },
        { provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService },
        {
            provide: TimesheetAndExpensesInstanceToken,
            // useExisting so the token resolves to the same providedIn:'root' singleton that
            // InitializationService initializes (a fresh useClass instance would be uninitialized).
            useExisting: environment.enableNgRx ? TimesheetAndExpensesNgrxService : TimesheetAndExpensesService,
        },
        {
            provide: ConfigInstanceToken,
            useExisting: environment.enableNgRx ? ConfigNgrxService : ConfigService,
        },
        {
            provide: ValueHelperInstanceToken,
            useExisting: environment.enableNgRx ? ValueHelperNgrxService : ValueHelperService,
        },
        {
            provide: UserMasterInstanceToken,
            useExisting: environment.enableNgRx ? UserMasterNgrxService : UserMasterService,
        },
        {
            provide: MeasurementToolsInstanceToken,
            useExisting: environment.enableNgRx ? MeasurementToolsNgrxService : MeasurementToolsService,
        },
        {
            provide: RecipientsInstanceToken,
            useExisting: environment.enableNgRx ? RecipientsNgrxService : LocalStorageRecipientsService,
        },
        {
            provide: BarcodeScannerInstanceToken,
            useExisting: environment.enableNgRx ? BarcodeScannerNgrxService : BarcodeScannerService,
        },
        {
            provide: NotificationStatusInstanceToken,
            useExisting: environment.enableNgRx ? NotificationStatusNgrxService : NotificationStatusService,
        },
        {
            provide: NotificationDataInstanceToken,
            useExisting: environment.enableNgRx ? NotificationNgrxService : NotificationService,
        },
        {
            provide: InputFieldPropertiesInstanceToken,
            useExisting: environment.enableNgRx ? InputFieldPropertiesNgrxService : InputFieldPropertiesService,
        },
        {
            provide: EscalationInstanceToken,
            useExisting: environment.enableNgRx ? EscalationNgrxService : EscalationService,
        },
        {
            provide: SoftwareLicencesInstanceToken,
            useExisting: environment.enableNgRx ? SoftwareLicencesNgrxService : SoftwareLicencesService,
        },
        {
            provide: PwaPushNotificationInstanceToken,
            useExisting: environment.enableNgRx ? PwaPushNotificationNgrxService : PwaPushNotificationService,
        },
        {
            provide: WeeklyWorkTimeInstanceToken,
            useExisting: environment.enableNgRx ? WeeklyWorkTimeNgrxService : WeeklyWorkTimeService,
        },
        {
            provide: DateManipulationCheckInstanceToken,
            useExisting: environment.enableNgRx ? DateManipulationCheckNgrxService : DateManipulationCheckService,
        },
    ],
})
export class AppModule {
    public constructor(private readonly translate: TranslateService) {
        this.translate.setDefaultLang(ConfigConstants.enLocale);
        this.translate.use(ConfigConstants.enLocale);
    }
}
